import React, { useEffect, useMemo, useState } from 'react';
import { Check, Download, FileDown, FileText } from 'lucide-react';
import { downloadMisReport } from '../../api/action-creator';
import './reports.css';

const HISTORY_KEY = 'carepay_report_history';
const REPORT_TYPES = [
  { value: 'all', label: 'Loan transactions' },
  { value: 'disburse', label: 'Disbursed & ready to disburse' },
];

const toInputDate = (date) => date.toISOString().split('T')[0];

const firstDayOfMonth = () => {
  const now = new Date();
  return toInputDate(new Date(now.getFullYear(), now.getMonth(), 1));
};

const convertDateFormat = (dt) => {
  if (!dt) return '';
  const [year, month, day] = dt.split('-');
  return `${day}-${month}-${year}`;
};

const formatGeneratedAt = (iso) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Just now';
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const loadHistory = () => {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const Reports = ({ clinics = [], minDate, maxDate }) => {
  const [startDate, setStartDate] = useState(firstDayOfMonth);
  const [endDate, setEndDate] = useState(toInputDate(new Date()));
  const [clinic, setClinic] = useState('');
  const [reportType, setReportType] = useState('all');
  const [history, setHistory] = useState(loadHistory);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, 12)));
  }, [history]);

  const ready = Boolean(startDate && endDate && new Date(startDate) <= new Date(endDate));
  const typeLabel = REPORT_TYPES.find((item) => item.value === reportType)?.label || 'Report';

  const monthLabel = useMemo(() => {
    if (!startDate) return '';
    return new Date(startDate).toLocaleDateString('en-GB', { month: 'long' });
  }, [startDate]);

  const downloadReport = (params, onSuccess) => {
    const doctorCode = params.doctorCode || localStorage.getItem('merchantCode') || localStorage.getItem('doctorId') || '';
    if (!doctorCode) {
      alert('Doctor code not found. Please login again.');
      return;
    }

    setDownloading(true);
    const resolvedType = doctorCode === 'CP3118' ? 'allTemplate1' : params.type;

    downloadMisReport(
      params.sDate,
      params.eDate,
      doctorCode,
      resolvedType,
      (result) => {
        setDownloading(false);
        if (onSuccess) onSuccess(result);
      },
      (err) => {
        setDownloading(false);
        alert(err.message || 'Failed to download report');
      }
    );
  };

  const handleGenerate = () => {
    if (!ready) {
      alert('Please select both start and end dates for the report');
      return;
    }

    const params = {
      sDate: convertDateFormat(startDate),
      eDate: convertDateFormat(endDate),
      type: reportType,
      doctorCode: localStorage.getItem('merchantCode') || localStorage.getItem('doctorId') || '',
    };

    downloadReport(params, (result) => {
      setHistory((prev) =>
        [
          {
            id: Date.now(),
            name: `${monthLabel} ${typeLabel.toLowerCase()} report`,
            generatedAt: new Date().toISOString(),
            format: result?.format || 'CSV',
            clinic: clinic || 'All clinics',
            sDate: params.sDate,
            eDate: params.eDate,
            type: params.type,
            doctorCode: params.doctorCode,
          },
          ...prev,
        ].slice(0, 12)
      );
    });
  };

  return (
    <div className="rp-page">
      <p className="rp-kicker">Manage</p>
      <div className="rp-head">
        <h1>Reports</h1>
        <p>Generate downloadable reports for your partner operations.</p>
      </div>

      <section className="rp-card">
        <div className="rp-card-top">
          <div className="rp-card-title">
            <span className="rp-icon">
              <FileDown size={18} />
            </span>
            <div>
              <h2>Report download</h2>
              <p>Choose a date range, clinic, and report type.</p>
            </div>
          </div>
          <span className={`rp-ready${ready ? ' is-ready' : ''}`}>
            {ready ? (
              <>
                <Check size={14} />
                Ready to generate
              </>
            ) : (
              'Select a date range'
            )}
          </span>
        </div>

        <div className="rp-filters">
          <label>
            <span>From</span>
            <input
              type="date"
              value={startDate}
              min={minDate}
              max={endDate || maxDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>
          <label>
            <span>To</span>
            <input
              type="date"
              value={endDate}
              min={startDate || minDate}
              max={maxDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>
          {/* <label>
            <span>Clinic</span>
            <select value={clinic} onChange={(e) => setClinic(e.target.value)}>
              <option value="">All clinics</option>
              {clinics.map((item, index) => (
                <option key={item?.doctorId || index} value={item?.clinicName || ''}>
                  {item?.clinicName}
                </option>
              ))}
            </select>
          </label> */}
          <label>
            <span>Report type</span>
            <select value={reportType} onChange={(e) => setReportType(e.target.value)}>
              {REPORT_TYPES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="rp-download" onClick={handleGenerate} disabled={!ready || downloading}>
            <Download size={16} />
            {downloading ? 'Downloading…' : 'Download report'}
          </button>
        </div>
      </section>

      <section className="rp-history">
        <div className="rp-history-head">
          <h2>Available reports</h2>
          <p>Recent exports are available for your team.</p>
        </div>
        {history.length === 0 ? (
          <div className="rp-empty">No reports generated yet. Choose a range and download one.</div>
        ) : (
          <ul>
            {history.map((item) => (
              <li key={item.id}>
                <span className="rp-file-icon">
                  <FileText size={18} />
                </span>
                <div>
                  <strong>{item.name}</strong>
                  <span>
                    Generated {formatGeneratedAt(item.generatedAt)} · {item.format}
                    {item.clinic ? ` · ${item.clinic}` : ''}
                  </span>
                </div>
                <button
                  type="button"
                  disabled={downloading}
                  onClick={() =>
                    downloadReport({
                      sDate: item.sDate,
                      eDate: item.eDate,
                      type: item.type,
                      doctorCode: item.doctorCode,
                    })
                  }
                >
                  <Download size={15} />
                  Download
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default Reports;
